import { Checkbox, Col, Form, InputNumber } from 'antd';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { injectIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { Dropdown, Flex, FormField, Input, Row } from 'ui';
import useDeepCompareEffect from 'use-deep-compare-effect';
import RichTextEditor from '../../../ui/rich-text-editor';
import { standaloneTabKeys } from '../../../utils/app-configs';
import SeveritySelector from '../announcements/severity-selector';

const StyledRow = styled(Row)`
  margin-bottom: 30px;
`;

const StyledInputNumber = styled(InputNumber)`
  width: 60px;
  margin-right: 10px;

  .ant-input-number-input {
    padding: 0;
  }
`;

const AnnouncementTabPane = ({
  intl,
  tabKey,
  form,
  disabled,
}) => {
  const {
    standaloneMode,
  } = useSelector(state => state.standaloneMode);
  const isShown = Form.useWatch([`${tabKey}`, 'isShown'], form);
  const isTurnedOn = Form.useWatch('isTurnedOn');
  const [wizardSeverities, setWizardSeverities] = useState([]);
  const [wizardTemplates, setWizardTemplates] = useState([]);

  useDeepCompareEffect(() => {
    const wizardSeveritiesToSet = standaloneMode[`${tabKey}Announcement`]?.wizardSeverities;
    const wizardTemplatesToSet = standaloneMode[`${tabKey}Announcement`]?.wizardTemplates;

    if (wizardSeveritiesToSet) {
      setWizardSeverities(wizardSeveritiesToSet);
    }
    if (wizardTemplatesToSet) {
      setWizardTemplates(wizardTemplatesToSet);
    }
  }, [standaloneMode]);

  const isDisabledOrNotShown = !isShown || disabled;

  const isRequired = isShown && isTurnedOn;

  const handleTemplateChange = (templateId) => {
    const template = wizardTemplates?.find(t => t?.id === templateId);
    if (template) {
      form.setFieldsValue({
        [tabKey]: {
          template: template.id,
          title: template.title,
          message: template.message,
        },
      });
    }
  };

  return (
    <>
      <StyledRow>
        <Col span={24}>
          <Flex aic>
            <FormField
              name={[`${tabKey}`, 'isShown']}
              valuePropName='checked'
            >
              <Checkbox
                disabled={tabKey === standaloneTabKeys.during || disabled}
              >
                {intl.formatMessage({
                  id: `standaloneMode.SHOW_${tabKey.toUpperCase()}_FOR`,
                })}
              </Checkbox>
            </FormField>
            {tabKey !== standaloneTabKeys.during &&
              <>
                <FormField
                  name={[`${tabKey}`, 'forDays']}
                >
                  <StyledInputNumber
                    min={1}
                    disabled={isDisabledOrNotShown}
                  />
                </FormField>
                {intl.formatMessage({ id: 'standaloneMode.DAYS' })}
              </>
            }
          </Flex>
        </Col>
      </StyledRow>
      <StyledRow>
        <Col span={24}>
          <FormField
            label={intl.formatMessage({
              id: 'announcements.edit.TEMPLATE',
            })}
            name={[`${tabKey}`, 'template']}
            initialValue={null}
            wrapperCol={{ span: 7 }}
          >
            <Dropdown
              values={standaloneMode[`${tabKey}Announcement`]?.wizardTemplates || []}
              valueKey='id'
              valueLabel='name'
              disabled={isDisabledOrNotShown}
              onSelect={handleTemplateChange}
            />
          </FormField>
        </Col>
      </StyledRow>
      <StyledRow>
        <Col span={24}>
          <FormField
            label={intl.formatMessage({
              id: 'announcements.TITLE',
            })}
            name={[`${tabKey}`, 'title']}
            initialValue=''
            rules={[{
              required: isRequired,
              message: intl.formatMessage({ id: 'rules.ANNOUNCEMENT_TITLE' }),
            }]}
          >
            <Input disabled={isDisabledOrNotShown} />
          </FormField>
        </Col>
      </StyledRow>
      <StyledRow>
        <Col span={24}>
          <FormField
            name={[`${tabKey}`, 'hasBanner']}
            initialValue={false}
            valuePropName='checked'
          >
            <Checkbox
              disabled={tabKey === standaloneTabKeys.during || isDisabledOrNotShown}
            >
              {intl.formatMessage({
                id: tabKey === standaloneTabKeys.during ?
                  'standaloneMode.BANNER_ALWAYS' :
                  'announcements.edit.ACTIVATE_BANNER',
              })}
            </Checkbox>
          </FormField>
        </Col>
      </StyledRow>
      <StyledRow>
        <Col span={24}>
          <FormField
            name={[`${tabKey}`, 'message']}
            initialValue=''
            rules={[{
              required: isRequired,
              message: intl.formatMessage({ id: 'rules.ANNOUNCEMENT_DESCRIPTION' }),
            }]}
          >
            <RichTextEditor width='100%' disabled={isDisabledOrNotShown} />
          </FormField>
        </Col>
      </StyledRow>
      <StyledRow>
        <Col span={24}>
          <FormField
            label={intl.formatMessage({
              id: 'announcements.SEVERITY',
            })}
            name={[`${tabKey}`, 'severity']}
            initialValue={null}
            rules={[{
              required: isRequired,
              message: intl.formatMessage({ id: 'rules.ANNOUNCEMENT_SEVERITY' }),
            }]}
            wrapperCol={{ span: 7 }}
          >
            <SeveritySelector
              options={wizardSeverities}
              disabled={isDisabledOrNotShown}
            />
          </FormField>
        </Col>
      </StyledRow>
      <StyledRow>
        <Col span={24}>
          <FormField
            name={[`${tabKey}`, 'isPinned']}
            valuePropName='checked'
            initialValue={false}
          >
            <Checkbox disabled={isDisabledOrNotShown}>
              {intl.formatMessage({
                id: 'standaloneMode.PIN_ANNOUNCEMENT',
              })}
            </Checkbox>
          </FormField>
        </Col>
      </StyledRow>
    </>
  );
};

AnnouncementTabPane.propTypes = {
  intl: PropTypes.object,
  tabKey: PropTypes.string,
  form: PropTypes.object,
  disabled: PropTypes.bool,
};

export default injectIntl(AnnouncementTabPane);
